/**
 * One session's conversation, from the runtime's own event stream.
 *
 * Story VC-016. `agent_runtime_lld.md` §29 (the events), §27.3 (the response
 * lifecycle), §28.3 (snapshots), §40.2 and §40.3 (the stream and its
 * resume).
 *
 * # This is used by both screens, and that is the point
 *
 * A chat session has nothing but this. A *voice* session has it as well:
 * `voice_server_lld.md` §24.2 sends every response revision to two sinks,
 * and the second is the session's own subscribers — the same registry the
 * text transport attaches to, so a browser watching a voice session receives
 * the object a browser watching a typed one receives. The call screen
 * therefore reads its answer from here rather than from the voice socket's
 * `response.updated`, and the two are byte-identical by VS-009's own
 * acceptance. One binding, one renderer, one contract.
 *
 * # There is no turn state here
 *
 * The runtime's events *are* the turn state. This file holds no timer, makes
 * no decision about when an answer is finished, and never infers a state
 * from the absence of an event — `turn_started` opens and one of §10.1's
 * three terminals closes.
 *
 * # What was already said comes from somewhere else
 *
 * §40.9's transcript, read to the end *before* this stream opens (§40.9.6).
 * Not the other way round and not concurrently: a client that opened the
 * stream first would apply live revisions to a thread it then overwrote with
 * an older page. The overlap is not a duplicate to suppress — both sides key
 * an agent turn by `turn_id`, so a replayed revision replaces the seeded
 * entry, and the user's side is never duplicated because no event carries
 * it. `session/thread.js` holds the merge, and has the tests.
 *
 * VC-016 had no such source and reconstructed the user's half from
 * `localStorage`, rendered ahead of the agent's: every question, then every
 * answer. VC-024 is that defect, and this paragraph is its fix.
 *
 * # Delivery is at-least-once
 *
 * §I.3.10. The publisher re-delivers what §28.1's backlog already carried,
 * so a client dedups on `event_id` rather than assuming. A snapshot is
 * applied by *replacement* and never merged (`agent_runtime_hld.md` §22.4):
 * a client that missed deltas converges on the next snapshot, which only
 * works if it stops trying to reconstruct one.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { postMessage, readEvents, readTranscript } from "../api/runtime.js";
import { rememberTitle } from "./store.js";
import {
  mergeSeed,
  prose,
  resumeKey,
  seedTurns,
  settle,
  upsertAgent,
  upsertSpoken,
} from "./thread.js";
import { uuid } from "./uuid.js";

/** @typedef {import("./thread.js").Turn} Turn */

/**
 * @param {{ config: import("../api/runtime.js").RuntimeConfig,
 *           sessionId: string | null,
 *           makeDecoder: (() => { push(chunk: Uint8Array): string[] }) | null,
 *           initialTurns?: Turn[] }} options
 */
export function useConversation({
  config,
  sessionId,
  makeDecoder,
  initialTurns = [],
}) {
  const [turns, setTurns] = useState(initialTurns);
  const [busy, setBusy] = useState(false);
  const [closed, setClosed] = useState(false);
  const [connection, setConnection] = useState("connecting");
  const [error, setError] = useState(null);

  // Event ids already applied, and the last one seen. The set is a ref
  // rather than state because nothing renders from it and a `Set` that went
  // through `useState` would re-render on every event to change nothing.
  const seen = useRef(new Set());
  const lastEventId = useRef(null);

  useEffect(() => {
    if (!sessionId || !makeDecoder) {
      return undefined;
    }
    const abort = new AbortController();
    setConnection("connecting");

    // One decoder per *stream*, made here. It holds the bytes of an event
    // that has not finished arriving, so a decoder reused across two streams
    // starts the second one with the tail of the first — which is the
    // framing bug `agent-sse` exists to prevent, reintroduced by a cache.
    const decoder = makeDecoder();

    const apply = (envelope) => {
      // The first frame may be a resume, carrying what was already durable
      // when the stream opened (§28.1). One code path: replay its backlog
      // and carry on (§40.2).
      if (envelope.event_type === "resume") {
        setConnection("live");
        if (envelope.snapshot) {
          // Keyed by its *turn*, exactly as a `response_projected` is
          // (§40.3, §28.0). It used to be filed under a key of this
          // client's own invention, which nothing ever superseded — so a
          // resumed session rendered the agent's last answer twice, once as
          // the durable checkpoint and once as itself. The key is the whole
          // of the fix; the replay that follows carries the same turn at a
          // later revision and replaces this.
          //
          // It is often *older* than the replay — §28.3 checkpoints on a
          // count and an interval, so a short answer persists only its
          // first revision — which is why it is applied before the events
          // and never after them.
          const { value } = envelope.snapshot;
          setTurns((current) =>
            upsertAgent(current, resumeKey(envelope.snapshot), {
              snapshot: value,
              text: prose(value),
              final: true,
            }),
          );
        }
        for (const event of envelope.events ?? []) {
          apply(event);
        }
        return;
      }

      if (envelope.event_id) {
        if (seen.current.has(envelope.event_id)) {
          return;
        }
        seen.current.add(envelope.event_id);
        lastEventId.current = envelope.event_id;
      }
      setConnection("live");

      switch (envelope.event_type) {
        case "turn_started":
          setBusy(true);
          break;
        case "turn_completed":
        case "turn_failed":
        case "turn_cancelled":
          setBusy(false);
          // Whatever was still arriving has stopped arriving. Marked final
          // rather than left with a caret blinking at a turn that ended.
          setTurns((current) =>
            settle(current, envelope.turn_id, envelope.event_type),
          );
          if (envelope.event_type === "turn_failed") {
            setError("the turn failed; the server log says why");
          }
          break;
        case "inference_failed":
          // Worth surfacing: it is what a misconfigured provider produces,
          // and a silent transcript is the symptom people report.
          setError(
            `inference failed (${envelope.payload?.class ?? "unknown"})`,
          );
          break;
        case "session_closed":
          setClosed(true);
          setBusy(false);
          break;
        case "response_projected":
          applyResponse(setTurns, envelope);
          break;
        default:
          break;
      }
    };

    // §40.9.6's two steps, in one sequence because the order is the
    // contract: page the transcript to the end, *then* open the stream.
    const run = async () => {
      try {
        const entries = await readTranscript(config, sessionId, {
          signal: abort.signal,
        });
        if (abort.signal.aborted) {
          return;
        }
        setTurns((current) => mergeSeed(current, seedTurns(entries)));
      } catch (failure) {
        if (abort.signal.aborted) {
          return;
        }
        // Reported, not fatal. A conversation that will not load is a screen
        // missing its history; a screen that refuses to open is a session
        // the user cannot continue, and the second is worse.
        setError(`what was said before could not be read: ${failure.message}`);
      }
      if (abort.signal.aborted) {
        return;
      }

      try {
        await readEvents(config, sessionId, {
          decoder,
          onEvent: apply,
          lastEventId: lastEventId.current,
          signal: abort.signal,
        });
        if (!abort.signal.aborted) {
          // The server ended the stream. Not an error — a closed session
          // does this — but the screen should stop claiming to be live.
          setConnection("ended");
        }
      } catch (failure) {
        if (abort.signal.aborted) {
          return;
        }
        setConnection("error");
        setError(`the event stream stopped: ${failure.message}`);
      }
    };
    void run();

    return () => abort.abort();
  }, [config, sessionId, makeDecoder]);

  /** Sends one typed message, and shows it. */
  const send = useCallback(
    async (text) => {
      const trimmed = text.trim();
      if (!trimmed || !sessionId) {
        return;
      }
      setError(null);

      // Rendered here rather than from an event, because no event carries
      // it: §29's `user_message_received` is `{duplicate, message_id,
      // priority}`, and the text lives in the conversation history the agent
      // reads. The client already knows what it typed, and showing it is not
      // a claim that the server accepted it — the catch below withdraws it
      // if it did not.
      const id = `user:${uuid()}`;
      setTurns((current) => [
        ...current,
        { id, speaker: "user", text: trimmed, final: true },
      ]);

      try {
        await postMessage(config, sessionId, trimmed);
        // The title, and nothing else. The turn itself is the runtime's to
        // keep now, and a transcript in `localStorage` is content with no
        // retention policy over it (SEC-003).
        rememberTitle(sessionId, trimmed);
      } catch (failure) {
        // Put it back rather than lose it: an input box that silently
        // empties is a message the user believes they sent (`AGENTS.md`
        // §I.3.3 is the server's half of the same promise).
        setTurns((current) => current.filter((turn) => turn.id !== id));
        setError(`not sent: ${failure.message}`);
        throw failure;
      }
    },
    [config, sessionId],
  );

  /** Adds a turn this stream will never carry — a spoken one. */
  const observe = useCallback((speaker, view) => {
    setTurns((current) => upsertSpoken(current, speaker, view));
  }, []);

  return useMemo(
    () => ({ turns, busy, closed, connection, error, send, observe, setError }),
    [turns, busy, closed, connection, error, send, observe],
  );
}

/**
 * Applies one `response_projected`.
 *
 * The bubble is keyed by the *turn*, not by the response: §27.3 gives one
 * turn a partial response and then a final one, with different ids, and two
 * bubbles would show the agent saying "let me check" and then, separately,
 * the answer. What a reader sees is one thing the agent said, arriving.
 *
 * The turn comes from the *payload* (§28.0), not from the envelope. Both
 * carry it here and the envelope is older, but the payload's is the one a
 * voice session has — the socket delivers this object with no envelope
 * around it — so reading the payload is what makes this one binding for both
 * transports rather than two that agree today.
 */
function applyResponse(setTurns, envelope) {
  const payload = envelope.payload ?? {};
  const snapshot = payload.snapshot;
  if (snapshot === undefined || snapshot === null) {
    // `snapshots: checkpointed` sends deltas between checkpoints (§28.3).
    // This client reads snapshots only, which the shipped document's
    // `every_update` guarantees; a deployment that checkpoints needs the
    // delta path, and that is not this story.
    return;
  }
  // The payload first, the envelope second, the response last. A response
  // with no turn is not attributable and gets its own bubble rather than
  // joining whichever turn happens to be open — which is the correlation by
  // timing that §28.0 exists to replace.
  const key =
    payload.turn_id ?? envelope.turn_id ?? payload.response_id ?? "response";
  const final = payload.phase === "final";
  setTurns((current) =>
    upsertAgent(current, key, {
      snapshot,
      text: prose(snapshot),
      final,
    }),
  );
}
